/**
 * Tabs above a page that belongs to a family (familyTabs.ts): the pages the
 * sidebar no longer lists on their own (Close the day, Scan inbox, Letters,
 * Recall results, Referrals, ...) one click from their family's page. Only
 * tabs the person can open show; the office (and, for Documents, the
 * patient) carry over.
 */
import { Link, useLocation } from '@tanstack/react-router';
import { ContextMenu } from '@velora/ui';
import { AppWindow } from 'lucide-react';
import { inPopout, useOpenInWindow } from '../windows/PopoutHereButton.js';
import { familyFor } from './familyTabs.js';
import { useCanOpenPath } from './useCanOpenPath.js';

export function FamilyTabs() {
  const { pathname } = useLocation();
  const canOpen = useCanOpenPath();
  const fam = familyFor(pathname, canOpen);
  const openInWindow = useOpenInWindow();
  if (!fam) return null;
  const popout = inPopout();
  return (
    <nav
      className="shell-family"
      aria-label={fam.family.label}
      data-fill={fam.tabs.find((t) => t.path === fam.active)?.fills ? '' : undefined}
    >
      {fam.tabs.map((t) => {
        const link = (
          <Link
            key={t.path}
            to={t.path as never}
            search={
              ((prev: Record<string, unknown>) => ({
                office: prev['office'],
                ...(prev['patient'] ? { patient: prev['patient'] } : {}),
              })) as never
            }
            className="shell-family__tab"
            aria-current={t.path === fam.active ? 'page' : undefined}
            activeOptions={{ exact: true, includeSearch: false }}
            activeProps={{}}
          >
            {t.label}
          </Link>
        );
        // QA r57: right-click a module tab to open it in its own window.
        return popout ? (
          link
        ) : (
          <ContextMenu
            key={t.path}
            items={[
              {
                id: 'new-window',
                label: 'Open in new window',
                icon: <AppWindow size={14} />,
                onSelect: () => openInWindow(t.path, t.path, t.label),
              },
            ]}
          >
            {link}
          </ContextMenu>
        );
      })}
    </nav>
  );
}
